import { useTranslation } from "react-i18next";
import { resolveMutationErrorMessage } from "@/shared/api/error-message";
import { useSendPasswordReset } from "../use-send-password-reset";
import { resolvePasswordResetErrorMessage } from "../password-reset-error-message";
import { useVerifyPhone } from "../use-verify-phone";
import { resolveResultToast } from "../notification-result-message";
import type { AdminNotificationResult } from "../notification-types";
import type { UserDetail } from "../types";
import type { ActionToast } from "./use-user-action-toast";

type SetToast = (toast: ActionToast | null) => void;

/**
 * The inline actions of the user detail page (phone verification, password
 * reset) and the toast mapping of the dialog outcomes. Every outcome lands in
 * the page's single toast.
 */
export function useUserDetailActions(user: UserDetail | undefined, setToast: SetToast) {
  const { t } = useTranslation();
  const passwordReset = useSendPasswordReset();
  const verifyPhone = useVerifyPhone();

  const handleVerifyPhone = () => {
    // Guard mirrors the button's disabled state — never POST for a user
    // with no phone or an already-verified one.
    if (!user || !user.phone || user.phoneVerified) return;
    setToast(null);
    verifyPhone.mutate(user.id, {
      onSuccess: () => {
        setToast({ tone: "success", message: t("users.verifyPhone.success") });
      },
      onError: (err) => {
        setToast({
          tone: "error",
          message: resolveMutationErrorMessage(err, t, {
            forbiddenKey: "users.errors.forbidden",
            serverPrefixKey: "users.errors.serverPrefix",
            genericKey: "users.verifyPhone.error",
          }),
        });
      },
    });
  };

  const handleSendPasswordReset = () => {
    if (!user) return;
    setToast(null);
    passwordReset.mutate(user.email, {
      onSuccess: () => {
        setToast({
          tone: "success",
          message: t("users.passwordReset.success", { email: user.email }),
        });
      },
      onError: (err) => {
        setToast({ tone: "error", message: resolvePasswordResetErrorMessage(err, t) });
      },
    });
  };

  const handleMutationSuccess = (key: string) => {
    setToast({ tone: "success", message: t(key) });
  };

  const handleNotificationSent = (result: AdminNotificationResult) => {
    const { tone, key, values } = resolveResultToast(result);
    setToast({ tone, message: t(key, values) });
  };

  return {
    verifyPhonePending: verifyPhone.isPending,
    passwordResetPending: passwordReset.isPending,
    handleVerifyPhone,
    handleSendPasswordReset,
    handleMutationSuccess,
    handleNotificationSent,
  };
}
